import { useEffect, useRef, useState } from 'react';
import {
  Button,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  InlineDrawer,
  OverlayDrawer,
  Spinner,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Dismiss20Regular, Open20Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { getHelpCenterUrls, type HelpCenterUrls } from '../../lib/api';
import { buildHelpCenterUrl, helpCenterOrigin, toAppLauncherLanguage } from '../../lib/appLauncherHandoff';

/** AppLauncher's address for this environment. The help entry is hidden when it is unset. */
function helpCenterPortalUrl(): string | undefined {
  return import.meta.env.VITE_APPLAUNCHER_PORTAL_URL;
}

export function isHelpCenterConfigured(): boolean {
  return helpCenterOrigin(helpCenterPortalUrl()) !== null;
}

/** Width of the docked panel. The app shell reserves the same width so its content reflows beside it. */
export const HELP_PANEL_WIDTH = 'clamp(360px, 38vw, 640px)';

/** The help page set in the hosting portal for a tenant role, or null for the default help space. */
export function helpPageForRole(urls: HelpCenterUrls | null, role: string | null | undefined): string | null {
  if (!urls) return null;
  if (role === 'TenantAdmin') return urls.tenantAdmin;
  if (role === 'Manager') return urls.manager;
  return urls.subscriber;
}

interface HelpPanelProps {
  open: boolean;
  onClose: () => void;
  /** The role the app is showing; an admin previewing another role gets that role's help. */
  role?: string | null | undefined;
  /** Take the whole viewport as an overlay instead of docking beside the app, for phone-sized screens. */
  fullScreen?: boolean;
}

const useStyles = makeStyles({
  // Docked beside the app rather than over it, so the reader can keep working while following an article.
  // Fixed to the viewport so pages keep scrolling the window (Contacts loads more on window scroll).
  docked: {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    width: HELP_PANEL_WIDTH,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow16,
  },
  fullScreen: {
    width: '100vw',
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
  },
  body: {
    position: 'relative',
    display: 'flex',
    padding: 0,
    // The frame scrolls itself; a second scrollbar on the drawer body would only nest them.
    overflow: 'hidden',
  },
  frame: {
    flexGrow: 1,
    width: '100%',
    height: '100%',
    border: 'none',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  loading: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colorNeutralBackground1,
  },
});

/**
 * QuickDial's space in the AppLauncher help centre, framed in a panel docked to the right of the app so
 * readers can keep using the app while they read (AppLauncher `docs/HELP_EMBED.md`). On phones it is a
 * full-screen overlay instead.
 *
 * The frame address is fixed when the panel opens. Later theme or language changes go to the frame as an
 * `exobe-help:set` message instead, so the reader keeps their place in the article.
 */
export function HelpPanel({ open, onClose, role, fullScreen = false }: HelpPanelProps) {
  const styles = useStyles();
  const { t, i18n } = useTranslation();
  const auth = useAuth();
  const { effectiveMode } = useTheme();
  const frameRef = useRef<HTMLIFrameElement>(null);
  const framePageUrlRef = useRef<string | null | undefined>(undefined);
  const [frameSrc, setFrameSrc] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [ready, setReady] = useState(false);
  // Undefined until the per-role pages have been read; null when they could not be.
  const [helpUrls, setHelpUrls] = useState<HelpCenterUrls | null | undefined>(undefined);

  const portalUrl = helpCenterPortalUrl();
  const origin = helpCenterOrigin(portalUrl);
  const username = auth.user?.upn ?? auth.azureAccount?.username;
  const language = toAppLauncherLanguage(i18n.language);
  const pageUrl = helpPageForRole(helpUrls ?? null, role);

  // Read on first opening and kept for the session. A failed read opens the default space and is retried
  // on the next opening.
  useEffect(() => {
    if (!open || helpUrls !== undefined) return;

    let cancelled = false;
    getHelpCenterUrls()
      .then((urls) => { if (!cancelled) setHelpUrls(urls); })
      .catch(() => { if (!cancelled) setHelpUrls(null); });

    return () => { cancelled = true; };
  }, [open, helpUrls]);

  useEffect(() => {
    if (!open) {
      setFrameSrc(null);
      setLoaded(false);
      setReady(false);
      framePageUrlRef.current = undefined;
      setHelpUrls((current) => (current === null ? undefined : current));
      return;
    }

    // Wait for the role's page, so the frame does not load the default space and then jump.
    if (helpUrls === undefined) return;

    const nextFrameSrc = buildHelpCenterUrl({
      portalUrl,
      language,
      username,
      embedded: { theme: effectiveMode },
      pageUrl,
    });

    // A role change selects a different page and must start a fresh frame. Theme and language changes
    // retain the current article and are sent to the existing frame instead.
    if (framePageUrlRef.current !== pageUrl) {
      framePageUrlRef.current = pageUrl;
      setFrameSrc(nextFrameSrc);
      setLoaded(false);
      setReady(false);
    } else {
      setFrameSrc((current) => current ?? nextFrameSrc);
    }
  }, [open, portalUrl, language, username, effectiveMode, helpUrls, pageUrl]);

  useEffect(() => {
    if (!open || !origin) return;

    const handleMessage = (event: MessageEvent) => {
      if (event.source !== frameRef.current?.contentWindow || event.origin !== origin) return;
      if (event.data?.type === 'exobe-help:ready') {
        setReady(true);
        frameRef.current?.contentWindow?.postMessage(
          { type: 'exobe-help:set', theme: effectiveMode, lang: language },
          origin,
        );
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [open, origin, effectiveMode, language]);

  useEffect(() => {
    if (!ready || !origin) return;
    frameRef.current?.contentWindow?.postMessage({ type: 'exobe-help:set', theme: effectiveMode, lang: language }, origin);
  }, [ready, origin, effectiveMode, language]);

  const newTabUrl = buildHelpCenterUrl({ portalUrl, language, username, pageUrl });

  const content = (
    <>
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <div className={styles.actions}>
              {newTabUrl && (
                <Button
                  as="a"
                  appearance="subtle"
                  href={newTabUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  icon={<Open20Regular />}
                  aria-label={t('help.openInNewTab')}
                  title={t('help.openInNewTab')}
                />
              )}
              <Button
                appearance="subtle"
                icon={<Dismiss20Regular />}
                aria-label={t('help.close')}
                title={t('help.close')}
                onClick={onClose}
              />
            </div>
          }
        >
          {t('help.title')}
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody className={styles.body}>
        {frameSrc && (
          <iframe
            ref={frameRef}
            className={styles.frame}
            src={frameSrc}
            title={t('help.frameTitle')}
            onLoad={() => setLoaded(true)}
          />
        )}
        {!loaded && (
          <div className={styles.loading}>
            <Spinner size="large" label={t('help.loading')} />
          </div>
        )}
      </DrawerBody>
    </>
  );

  if (fullScreen) {
    return (
      <OverlayDrawer
        className={styles.fullScreen}
        position="end"
        open={open}
        onOpenChange={(_, data) => { if (!data.open) onClose(); }}
      >
        {content}
      </OverlayDrawer>
    );
  }

  return (
    <InlineDrawer className={styles.docked} position="end" separator open={open} aria-label={t('help.title')}>
      {content}
    </InlineDrawer>
  );
}
